/**
 * Click-to-set-location map for the property create/edit form — a write
 * companion to the read-only `<PropertyMap>`, same Leaflet/OpenStreetMap
 * setup, but with a click handler instead of a fixed marker. Syncs
 * bidirectionally with the plain lat/lng text inputs already on the form:
 * clicking the map updates them, and typing into them recenters the map
 * (`MapContainer`'s own `center` prop only applies on first mount, so an
 * external position change needs an explicit `map.setView` via `useMap`).
 */
import { useEffect } from 'react';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';

const defaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

/** Nairobi — a reasonable default view for a Kenya-focused property list with no coordinates yet. */
const DEFAULT_CENTER: [number, number] = [-1.286389, 36.817223];
const DEFAULT_ZOOM = 6;
const PICKED_ZOOM = 15;

export interface LocationPickerProps {
  latitude: number | null;
  longitude: number | null;
  onPick: (latitude: number, longitude: number) => void;
}

function ClickHandler({ onPick }: { onPick: (latitude: number, longitude: number) => void }): null {
  useMapEvents({
    click(event) {
      onPick(event.latlng.lat, event.latlng.lng);
    },
  });
  return null;
}

function RecenterOnChange({ position }: { position: [number, number] | null }): null {
  const map = useMap();
  useEffect(() => {
    if (position !== null) map.setView(position, map.getZoom());
  }, [position, map]);
  return null;
}

export function LocationPicker({ latitude, longitude, onPick }: LocationPickerProps): React.JSX.Element {
  const position: [number, number] | null =
    latitude !== null && longitude !== null && Number.isFinite(latitude) && Number.isFinite(longitude)
      ? [latitude, longitude]
      : null;

  return (
    <div className="location-picker">
      <MapContainer
        center={position ?? DEFAULT_CENTER}
        zoom={position !== null ? PICKED_ZOOM : DEFAULT_ZOOM}
        scrollWheelZoom={false}
        className="location-picker__map"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <ClickHandler onPick={onPick} />
        <RecenterOnChange position={position} />
        {position !== null && <Marker position={position} icon={defaultIcon} />}
      </MapContainer>
      <p className="location-picker__hint">Click the map to set (or update) the property location.</p>
    </div>
  );
}

export default LocationPicker;
